import React, { useEffect, useMemo, useState } from "react";
import { useAuth } from "../context/AuthContext";
import type { UserRole } from "../types";
import LoginPage from "../pages/LoginPage";
import { getDefaultRouteForRole, logNav, ROUTES } from "../routeMap";

interface ProtectedRouteProps {
  children: React.ReactNode;
  allowedRoles?: Array<UserRole | string>;
}

type CanonRole = "client" | "firmowner" | "manager" | "taxpro" | "staff" | null;

const normalizeRole = (raw: any): CanonRole => {
  if (!raw) return null;

  const lower = raw.toString().trim().toLowerCase();

  // Client
  if (lower === "client") return "client";

  // Owner-ish
  if (lower === "firmowner" || lower === "firm owner" || lower === "owner" || lower === "admin") return "firmowner";

  // Manager
  if (lower === "manager") return "manager";

  // Tax pro / staff synonyms
  if (lower === "preparer" || lower === "taxpro" || lower === "tax_pro" || lower === "tax pro") return "taxpro";

  if (lower === "staff") return "staff";

  return null;
};

const safeSessionGet = (key: string) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, allowedRoles }) => {
  const { user, userDoc, appUser, staffRole, loading, firmId } = useAuth();

  const [hydrationGrace, setHydrationGrace] = useState(true);

  const isDemo = safeSessionGet("FILERSHUB_DEMO_SESSION") !== null;
  const demoRoleRaw = safeSessionGet("FILERSHUB_DEMO_ROLE");

  useEffect(() => {
    // In demo, do not hang around waiting for Firebase hydration
    const timeout = isDemo ? 150 : 2500;
    const t = setTimeout(() => setHydrationGrace(false), timeout);
    return () => clearTimeout(t);
  }, [user, isDemo]);

  const effectiveRole: CanonRole = useMemo(() => {
    // ✅ Demo role override always wins
    if (isDemo && demoRoleRaw) return normalizeRole(demoRoleRaw);

    return normalizeRole(staffRole) || normalizeRole(userDoc?.role) || normalizeRole(appUser?.role);
  }, [isDemo, demoRoleRaw, staffRole, userDoc?.role, appUser?.role]);

  const isAllowed = useMemo(() => {
    if (!allowedRoles?.length) return true;
    const normalizedAllowed = allowedRoles.map(normalizeRole);
    return normalizedAllowed.includes(effectiveRole);
  }, [allowedRoles, effectiveRole]);

  // Loading / hydration grace
  // ✅ In demo, do not require Firebase user hydration
  if (!isDemo && (loading || (user && !effectiveRole && hydrationGrace))) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-slate-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-brand-200 border-t-brand-600 rounded-full animate-spin" />
          <p className="text-slate-500 font-medium animate-pulse text-sm">Verifying Workspace Identity...</p>
        </div>
      </div>
    );
  }

  // Not signed in
  // ✅ Demo bypass: allow entry without Firebase auth
  if (!user && !isDemo) return <LoginPage />;

  // Signed in (or demo), but forbidden
  if (allowedRoles?.length && !isAllowed) {
    const homeView = getDefaultRouteForRole(effectiveRole || "client");
    const search = window.location.search;

    const locId = firmId || safeSessionGet("FILERSHUB_SSO_LOC");
    const pathPart = ROUTES[homeView] || "dashboard";

    const targetPath = locId ? `/location/${locId}/${pathPart}` : `/${pathPart}`;

    return (
      <div className="h-screen flex items-center justify-center bg-slate-50 p-6 font-sans">
        <div className="bg-white p-10 rounded-3xl shadow-xl max-w-lg w-full border border-red-100 animate-fade-in">
          <div className="w-20 h-20 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-6 text-3xl">
            🚫
          </div>

          <h2 className="text-2xl font-bold text-slate-800 mb-2">Access Restricted</h2>
          <p className="text-slate-500 text-sm mb-8">
            You do not have permission to view this section. Role detected:{" "}
            <strong>{effectiveRole || "unknown"}</strong>.
          </p>

          <div className="flex flex-col gap-3">
            <button
              onClick={() => {
                logNav(
                  effectiveRole || "unknown",
                  window.location.pathname + window.location.search,
                  targetPath + search,
                  "ProtectedRoute.Escape"
                );
                window.location.href = `${targetPath}${search}`;
              }}
              className="w-full py-4 rounded-xl bg-brand-600 text-white font-bold hover:bg-brand-700 transition"
            >
              Return to Home
            </button>

            <button
              onClick={() => {
                logNav(
                  effectiveRole || "unknown",
                  window.location.pathname + window.location.search,
                  "/",
                  "ProtectedRoute.SignInAgain"
                );
                window.location.href = "/";
              }}
              className="w-full py-4 rounded-xl bg-slate-100 text-slate-600 font-bold hover:bg-slate-200 transition"
            >
              Sign In Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default ProtectedRoute;
